import { useEffect, useState } from "react";
import { Plus, Search, Users } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ClientCard } from "@/components/ClientCard";
import { ClientFormDialog } from "@/components/ClientFormDialog";
import { QuickInvoiceDialog } from "@/components/QuickInvoiceDialog";
import { api, errMsg } from "@/lib/api";

const MAX_CLIENTS = 120;

export default function ClientsPage() {
  const [clients, setClients] = useState(null);
  const [settings, setSettings] = useState({ tax_rate: 0 });
  const [q, setQ] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [invoicing, setInvoicing] = useState(null);
  const [drafts, setDrafts] = useState({});

  const loadDrafts = () => api.get("/invoices", { params: { status: "draft" } })
    .then((r) => setDrafts(Object.fromEntries(r.data.map((d) => [d.client_id, d]))));

  useEffect(() => {
    Promise.all([api.get("/clients"), api.get("/settings"), loadDrafts()])
      .then(([c, s]) => { setClients(c.data); setSettings(s.data); })
      .catch((e) => toast.error(errMsg(e)));
  }, []);

  const saved = (c) => setClients((list) => {
    const i = list.findIndex((x) => x.id === c.id);
    return i >= 0 ? list.map((x) => (x.id === c.id ? c : x)) : [...list, c].sort((a, b) => a.name.localeCompare(b.name));
  });

  const remove = async (c) => {
    if (!window.confirm(`Delete ${c.name}? Past invoices are kept.`)) return;
    try { await api.delete(`/clients/${c.id}`); setClients((l) => l.filter((x) => x.id !== c.id)); toast.success("Client deleted"); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const shown = (clients || []).filter((c) => `${c.name} ${c.address} ${c.service_description}`.toLowerCase().includes(q.toLowerCase()));
  const atLimit = !!clients && clients.length >= MAX_CLIENTS;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight">Clients</h1>
          <p className="text-sm text-muted-foreground mt-1">Pull up, tap once, invoice sent.</p>
        </div>
        <div className="flex flex-col items-start sm:items-end gap-1">
          <Button data-testid="client-add-new-button" onClick={() => { setEditing(null); setFormOpen(true); }} disabled={atLimit} className="h-11 bg-emerald-600 hover:bg-emerald-700 text-white font-bold">
            <Plus className="h-4 w-4 mr-2" /> Add Client
          </Button>
          {clients && (
            <span data-testid="client-limit-counter" className={`text-xs font-medium ${atLimit ? "text-red-600" : clients.length >= MAX_CLIENTS * 0.9 ? "text-amber-600" : "text-muted-foreground"}`}>
              {atLimit ? `Limit reached — ${MAX_CLIENTS} of ${MAX_CLIENTS} clients. Delete one to add another.` : `${clients.length} of ${MAX_CLIENTS} clients`}
            </span>
          )}
        </div>
      </div>

      <div className="relative">
        <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input data-testid="client-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, address or service" className="pl-9 h-11" />
      </div>

      {clients === null ? (
        <div className="text-sm text-muted-foreground">Loading clients...</div>
      ) : shown.length === 0 ? (
        <div data-testid="clients-empty-state" className="surface p-10 flex flex-col items-start gap-3">
          <div className="h-12 w-12 rounded-full bg-emerald-50 dark:bg-emerald-950/50 grid place-items-center"><Users className="h-6 w-6 text-emerald-600" /></div>
          <h3 className="text-lg font-semibold">{q ? "No clients match your search" : "Add your first client"}</h3>
          <p className="text-sm text-muted-foreground max-w-md">Save each client's address, standard job and price once. After that, invoicing is a single tap from the driveway.</p>
          {!q && <Button onClick={() => setFormOpen(true)} variant="outline" className="mt-1">Add a client</Button>}
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {shown.map((c) => (
            <ClientCard key={c.id} client={c} draft={drafts[c.id]} defaultTax={settings.tax_rate}
              onInvoice={() => setInvoicing(c)} onEdit={() => { setEditing(c); setFormOpen(true); }} onDelete={() => remove(c)} />
          ))}
        </div>
      )}

      <ClientFormDialog open={formOpen} onOpenChange={setFormOpen} client={editing} defaultTax={settings.tax_rate} onSaved={saved} />
      <QuickInvoiceDialog client={invoicing} draft={invoicing ? drafts[invoicing.id] : null} defaultTax={settings.tax_rate} onClose={() => setInvoicing(null)} onCreated={loadDrafts} />
    </div>
  );
}
